import { z } from 'zod';
import { EmailInboxMessageSchema } from './email-intake.js';
import { RdVerifyResultSchema } from './rd-verify.js';
export const DocIngestChannelSchema = z.enum(['UPLOAD', 'EMAIL', 'SCAN', 'LINE']);
export const DocIngestStatusSchema = z.enum([
    'UPLOADED',
    'EXTRACTING',
    'EXTRACTED',
    'REVIEWING',
    'PENDING_APPROVAL',
    'POSTED',
    'REJECTED',
    'FAILED',
]);
export const VatCreditStatusSchema = z.enum(['CLAIMABLE', 'SUSPENSE']);
export const FieldConfidenceSchema = z.enum(['high', 'med', 'low']);
export const ExtractedFieldSchema = z.object({
    value: z.string().nullable(),
    confidence: FieldConfidenceSchema,
    sourceSnippet: z.string().nullable(),
    page: z.number().int().positive().nullable(),
});
export const ExtractedLineSchema = z.object({
    description: z.string().nullable(),
    qty: z.number().nullable(),
    unitPrice: z.number().nullable(),
    amount: z.number().nullable(),
});
const PartySchema = z.object({
    name: ExtractedFieldSchema,
    address: ExtractedFieldSchema,
    taxId: ExtractedFieldSchema,
    branch: ExtractedFieldSchema,
});
export const ExtractedInvoiceSchema = z.object({
    docTitle: ExtractedFieldSchema,
    seller: PartySchema,
    buyer: PartySchema,
    invoiceNo: ExtractedFieldSchema,
    invoiceDate: ExtractedFieldSchema,
    subtotal: ExtractedFieldSchema,
    vatAmount: ExtractedFieldSchema,
    total: ExtractedFieldSchema,
    lineItems: z.array(ExtractedLineSchema),
});
export const ValidationSeveritySchema = z.enum(['critical', 'warning', 'info']);
export const ValidationCheckSchema = z.object({
    code: z.string(),
    severity: ValidationSeveritySchema,
    passed: z.boolean(),
    message: z.string(),
});
export const ValidationResultSchema = z.object({
    checks: z.array(ValidationCheckSchema),
    hasCritical: z.boolean(),
});
export const IngestProposalSchema = z.object({
    ingestId: z.uuid(),
    status: DocIngestStatusSchema,
    docUrl: z.string(),
    // M5 — a deferred SCAN proposal is returned at status EXTRACTING with neither
    // extracted nor validation yet (the overnight Batch API fills them later), so
    // both are nullable. The sync UPLOAD/EMAIL path always sets both (back-compat).
    extracted: ExtractedInvoiceSchema.nullable(),
    validation: ValidationResultSchema.nullable(),
    vatCreditStatus: VatCreditStatusSchema.nullable(),
    // M2-e: RD VAT-verify verdict for the seller (null when RD_VERIFY_ENABLED off
    // → no chip / no reason). previewReasons = the pre-accept detectAnomaly preview
    // (guardrails + RD reasons) so the explainability panel + chip render before accept.
    rdVerify: RdVerifyResultSchema.nullable().default(null),
    previewReasons: z.array(z.string()).default([]),
    // M4 — the joined email-source message when this doc arrived by EMAIL (null for
    // UPLOAD / when the proposal is rebuilt before a join exists). Drives the
    // EmailSourcePanel + the EMAIL_* anomaly explainability in the review two-pane.
    emailMessage: EmailInboxMessageSchema.nullable().default(null),
});
export const AcceptIngestLineSchema = z.object({
    description: z.string(),
    qty: z.number().positive().default(1),
    unitPrice: z.number().nonnegative(),
    expenseCode: z.string().min(4),
});
export const AcceptIngestSchema = z.object({
    vendorId: z.uuid(),
    billDate: z.iso.date(),
    dueDate: z.iso.date(),
    vatCreditStatus: VatCreditStatusSchema,
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    vendorInvoiceNo: z.string().optional(),
    lines: z.array(AcceptIngestLineSchema).min(1),
    // WHT INTENT ONLY (spec §15.4 / §15 HARD SAFETY): captured for the bill so the
    // certificate can be issued at PAYMENT by the existing WHT module. Never issues
    // a cert here. whtRate is a percent (0..30); whtBase is the THB base amount.
    whtIncomeType: z.string().max(40).nullable().optional(),
    whtRate: z.number().min(0).max(30).nullable().optional(),
    whtBase: z.number().nonnegative().nullable().optional(),
});
export const RejectIngestSchema = z.object({
    // Capped so an unbounded client string can't be written verbatim into the
    // DocumentIngest.errorMessage TEXT column (#8).
    reason: z.string().min(1).max(500).optional(),
});
// ============================================================
// Inbox list-row shapes (1c). Lightweight projection of a DocumentIngest +
// its extraction summary — NO full extraction blob. worstConfidence /
// hasCritical / hasWarning are DERIVED server-side from the persisted
// validationResult + extractedFields so the inbox can triage worst-first.
// ============================================================
export const IngestListRowSchema = z.object({
    ingestId: z.string(),
    status: DocIngestStatusSchema,
    channel: DocIngestChannelSchema,
    docTitle: z.string().nullable(),
    sellerName: z.string().nullable(),
    invoiceNo: z.string().nullable(),
    invoiceDate: z.string().nullable(),
    total: z.string().nullable(),
    vatCreditStatus: VatCreditStatusSchema.nullable(),
    worstConfidence: FieldConfidenceSchema, // 'high' | 'med' | 'low'
    hasCritical: z.boolean(),
    hasWarning: z.boolean(),
    uploadedAt: z.string(),
    uploadedBy: z.string(),
    apBillId: z.string().nullable(),
});
export const IngestListResponseSchema = z.object({
    rows: z.array(IngestListRowSchema),
    total: z.number().int().nonnegative(),
});
export const IngestListFiltersSchema = z.object({
    status: DocIngestStatusSchema.optional(),
    channel: DocIngestChannelSchema.optional(),
    vendor: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    q: z.string().optional(),
    take: z.number().int().positive().optional(),
    skip: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=doc-ingest.js.map